import dayjs from "dayjs";
import { rollingWindowRange, thisMonthRange, thisWeekRange, toIsoDate, todayUtc } from "@acto/web-common/dates";
import { DateRange, PeriodOption } from "../ui/DateRangeSelector/types";

type RollingPeriod = Exclude<PeriodOption, PeriodOption.THIS_WEEK | PeriodOption.THIS_MONTH | PeriodOption.CUSTOM>;

const ROLLING_PERIOD_DAYS: Record<RollingPeriod, number> = {
  [PeriodOption.LAST_MONTH]: 30,
  [PeriodOption.LAST_3_MONTHS]: 90,
  [PeriodOption.LAST_6_MONTHS]: 180,
  [PeriodOption.LAST_12_MONTHS]: 360,
};

export function periodToDateRange(period: PeriodOption, customDateRange?: DateRange): DateRange {
  switch (period) {
    case PeriodOption.THIS_WEEK:
      return thisWeekRange();
    case PeriodOption.THIS_MONTH:
      return thisMonthRange();
    case PeriodOption.CUSTOM:
      return customDateRange ?? rollingWindowRange(30);
    default:
      return rollingWindowRange(ROLLING_PERIOD_DAYS[period]);
  }
}

/**
 * Whether the window has fully elapsed, the precondition for comparing it against the same
 * window a year earlier. This week, this month and a custom range reaching into the future are
 * still filling up, so comparing them pits partial data against a complete year-ago window.
 */
export function isComparableWindow(period: PeriodOption, customDateRange?: DateRange): boolean {
  return periodToDateRange(period, customDateRange).endDate < toIsoDate(todayUtc());
}

export function getPeriodDateContext(period: PeriodOption): string {
  if (period === PeriodOption.THIS_WEEK || period === PeriodOption.THIS_MONTH || period === PeriodOption.CUSTOM) {
    return "";
  }

  const { startDate, endDate } = periodToDateRange(period);
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  const sameYear = start.year() === end.year();
  const startFormat = sameYear ? "MMM" : "MMM YY";

  return `${start.format(startFormat)} - ${end.format("MMM YY")}`;
}
